import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Backdrop,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
  Fade,
  Grow,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  addKnowledge,
  getAnswer,
  getAllKnowledge,
  updateKnowledge,
  deleteKnowledge,
  fetchWebsiteContent,
} from "../../redux/action/carrier";
import {
  HelpOutline as HelpIcon,
  LightbulbOutlined as IdeaIcon,
  Send as SendIcon,
  AddCircleOutline as AddIcon,
  CheckCircle as SuccessIcon,
  AutoAwesome as SparkleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Public as WebsiteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

const AIBot = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [activeTab, setActiveTab] = useState("ask");
  const [conversation, setConversation] = useState<
    Array<{
      customerQuery: string;
      AiResponse: string;
    }>
  >([]);
  const [knowledgeBase, setKnowledgeBase] = useState<
    Array<{
      question: string;
      answer: string;
    }>
  >([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState({
    originalQuestion: "",
    question: "",
    answer: "",
  });
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const carrierEmail = localStorage.getItem("email");
  const carrierId = localStorage.getItem("CarrierId");
  const [siteUrl, setSiteUrl] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState("");
  const [fetchedUrls, setFetchedUrls] = useState<
    Array<{
      site_url: string;
      knowledge: {
        answer: string;
      };
    }>
  >([]);

  useEffect(() => {
    if ((activeTab === "knowledge" || activeTab === "fetch") && carrierEmail) {
      fetchKnowledgeBase();
    }
  }, [activeTab, carrierEmail]);

  useEffect(() => {
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [conversation, container]);

  const fetchKnowledgeBase = async () => {
    if (!carrierEmail) {
      console.error("No carrier email found.");
      return;
    }
    setIsLoading(true);
    setLoadingMessage("Loading knowledge base...");
    try {
      const result = await dispatch<any>(getAllKnowledge(carrierEmail));
      const response = unwrapResult(result);
      setKnowledgeBase(response.knowledge || []);
      setFetchedUrls(response.site_knowledge || []);
    } catch (error) {
      console.error("Error fetching knowledge:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAskQuestion = async () => {
    if (!question || !carrierEmail) return;

    setIsLoading(true);
    setLoadingMessage("Analyzing your question...");
    try {
      const result = await dispatch<any>(
        getAnswer({
          email: carrierEmail,
          question,
        })
      );

      const response = unwrapResult(result);
      setAnswer(response.answer);
      setConversation((prev) => [
        ...prev,
        {
          customerQuery: question,
          AiResponse: response.answer,
        },
      ]);
      setQuestion("");
    } catch (error) {
      console.error("Error getting answer:", error);
      setAnswer("Sorry, I couldn't process your question. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddKnowledge = async () => {
    if (!newQuestion || !newAnswer || !carrierEmail) return;

    setIsAdding(true);
    setLoadingMessage("Saving to knowledge base...");

    try {
      const result = await dispatch<any>(
        addKnowledge({
          email: carrierEmail,
          question: newQuestion,
          answer: newAnswer,
        })
      );

      unwrapResult(result);
      setNewQuestion("");
      setNewAnswer("");
      setAnswer("Knowledge added successfully!");
      await fetchKnowledgeBase();
    } catch (error) {
      console.error("Error adding knowledge:", error);
      setAnswer("Failed to add knowledge. Please try again.");
    } finally {
      setIsAdding(false);
    }
  };

  const handleEditKnowledge = async () => {
    if (!currentEditItem.originalQuestion || !carrierEmail) return;

    setIsEditing(true);
    setLoadingMessage("Updating knowledge...");

    try {
      const result = await dispatch<any>(
        updateKnowledge({
          email: carrierEmail,
          question: currentEditItem.originalQuestion,
          newQuestion: currentEditItem.question,
          newAnswer: currentEditItem.answer,
        })
      );

      unwrapResult(result);
      setEditDialogOpen(false);
      await fetchKnowledgeBase();
      setTimeout(() => {
        setIsEditing(false);
      }, 1000);
    } catch (error) {
      console.error("Error updating knowledge:", error);
    } finally {
      setIsEditing(false);
    }
  };

  const handleDeleteKnowledge = async (question: string) => {
    if (!question || !carrierEmail) return;

    setIsDeleting(true);
    setLoadingMessage("Deleting knowledge...");

    try {
      const result = await dispatch<any>(
        deleteKnowledge({
          email: carrierEmail,
          question,
        })
      );

      unwrapResult(result);
      await fetchKnowledgeBase();
    } catch (error) {
      console.error("Error deleting knowledge:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFetchWebsite = async () => {
    if (!siteUrl || !carrierId) return;

    setIsFetching(true);
    setLoadingMessage("Fetching website content...");

    try {
      const result = await dispatch<any>(
        fetchWebsiteContent({
          carrierId: Number(carrierId),
          url: siteUrl,
        })
      );

      const response = unwrapResult(result);
      setFetchResult(response.content);
      setSiteUrl("");
    } catch (error) {
      console.error("Error fetching website:", error);
      setFetchResult("Failed to fetch website content. Please try again.");
    } finally {
      setIsFetching(false);
    }
  };

  const handleViewContent = (content: string) => {
    if (fetchResult === content) {
      setFetchResult("");
    } else {
      setFetchResult(content);
    }
  };

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          p: isMobile ? 2 : 4,
          maxWidth: "850px",
          margin: isMobile ? "1rem auto" : "2rem auto",
          backgroundColor: theme.palette.background.paper,
          borderRadius: "18px",
          boxShadow: theme.shadows[4],
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {(isLoading || isAdding || isEditing || isDeleting || isFetching) && (
          <Backdrop
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backdropFilter: "blur(5px)",
              backgroundColor: "rgba(0,0,0,0.2)",
            }}
            open={
              isLoading || isAdding || isEditing || isDeleting || isFetching
            }
          >
            <Grow
              in={
                isLoading || isAdding || isEditing || isDeleting || isFetching
              }
            >
              <Box
                sx={{
                  width: isMobile ? "80%" : "40%",
                  textAlign: "center",
                  p: isMobile ? 2 : 4,
                  backgroundColor: "rgba(0,0,0,0.8)",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <SparkleIcon
                  sx={{
                    fontSize: isMobile ? "2rem" : "3rem",
                    mb: 2,
                    color: theme.palette.primary.light,
                  }}
                />
                <LinearProgress
                  sx={{
                    height: "10px",
                    borderRadius: "6px",
                    width: "100%",
                    mb: 2,
                  }}
                />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  {loadingMessage}
                </Typography>
              </Box>
            </Grow>
          </Backdrop>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: isMobile ? 2 : 4,
            gap: isMobile ? 1 : 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              width: isMobile ? 40 : 60,
              height: isMobile ? 40 : 60,
              boxShadow: theme.shadows[2],
            }}
          >
            <IdeaIcon fontSize={isMobile ? "medium" : "large"} />
          </Avatar>
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.5px",
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: 1.2,
              }}
            >
              Smart Assistant
            </Typography>
            <Typography
              variant={isMobile ? "body2" : "subtitle1"}
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Your intelligent business knowledge partner
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            mb: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            gap: 1,
            overflowX: isMobile ? "auto" : "visible",
            pb: 1,
          }}
        >
          <Button
            variant={activeTab === "ask" ? "contained" : "text"}
            sx={{
              px: isMobile ? 1.5 : 3,
              py: 1,
              borderRadius: "8px 8px 0 0",
              fontWeight: 600,
              letterSpacing: "0.5px",
              textTransform: "none",
              boxShadow: activeTab === "ask" ? theme.shadows[1] : "none",
              color:
                activeTab === "ask" ? "#fff" : theme.palette.text.secondary,
              backgroundColor:
                activeTab === "ask"
                  ? theme.palette.primary.main
                  : "transparent",
              "&:hover": {
                backgroundColor:
                  activeTab === "ask"
                    ? theme.palette.primary.dark
                    : theme.palette.action.hover,
              },
              minWidth: isMobile ? "fit-content" : "auto",
            }}
            onClick={() => setActiveTab("ask")}
            startIcon={isMobile ? null : <HelpIcon />}
          >
            {isMobile ? "Ask" : "Ask Question"}
          </Button>
          <Button
            variant={activeTab === "add" ? "contained" : "text"}
            sx={{
              px: isMobile ? 1.5 : 3,
              py: 1,
              borderRadius: "8px 8px 0 0",
              fontWeight: 600,
              letterSpacing: "0.5px",
              textTransform: "none",
              boxShadow: activeTab === "add" ? theme.shadows[1] : "none",
              color:
                activeTab === "add" ? "#fff" : theme.palette.text.secondary,
              backgroundColor:
                activeTab === "add"
                  ? theme.palette.primary.main
                  : "transparent",
              "&:hover": {
                backgroundColor:
                  activeTab === "add"
                    ? theme.palette.primary.dark
                    : theme.palette.action.hover,
              },
              minWidth: isMobile ? "fit-content" : "auto",
            }}
            onClick={() => setActiveTab("add")}
            startIcon={isMobile ? null : <AddIcon />}
          >
            {isMobile ? "Add" : "Add Knowledge"}
          </Button>
          <Button
            variant={activeTab === "knowledge" ? "contained" : "text"}
            sx={{
              px: isMobile ? 1.5 : 3,
              py: 1,
              borderRadius: "8px 8px 0 0",
              fontWeight: 600,
              letterSpacing: "0.5px",
              textTransform: "none",
              boxShadow: activeTab === "knowledge" ? theme.shadows[1] : "none",
              color:
                activeTab === "knowledge"
                  ? "#fff"
                  : theme.palette.text.secondary,
              backgroundColor:
                activeTab === "knowledge"
                  ? theme.palette.primary.main
                  : "transparent",
              "&:hover": {
                backgroundColor:
                  activeTab === "knowledge"
                    ? theme.palette.primary.dark
                    : theme.palette.action.hover,
              },
              minWidth: isMobile ? "fit-content" : "auto",
            }}
            onClick={() => setActiveTab("knowledge")}
            startIcon={isMobile ? null : <HistoryIcon />}
          >
            {isMobile ? "Knowledge" : "Knowledge Base"}
          </Button>
          <Button
            variant={activeTab === "fetch" ? "contained" : "text"}
            sx={{
              px: isMobile ? 1.5 : 3,
              py: 1,
              borderRadius: "8px 8px 0 0",
              fontWeight: 600,
              letterSpacing: "0.5px",
              textTransform: "none",
              boxShadow: activeTab === "fetch" ? theme.shadows[1] : "none",
              color:
                activeTab === "fetch" ? "#fff" : theme.palette.text.secondary,
              backgroundColor:
                activeTab === "fetch"
                  ? theme.palette.primary.main
                  : "transparent",
              "&:hover": {
                backgroundColor:
                  activeTab === "fetch"
                    ? theme.palette.primary.dark
                    : theme.palette.action.hover,
              },
              minWidth: isMobile ? "fit-content" : "auto",
            }}
            onClick={() => setActiveTab("fetch")}
            startIcon={isMobile ? null : <SearchIcon />}
          >
            {isMobile ? "Web AI" : "Web-Analyzer AI"}
          </Button>
        </Box>

        {activeTab === "ask" && (
          <Paper
            sx={{
              p: isMobile ? 2 : 4,
              mb: 3,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.default,
              boxShadow: "none",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {conversation.length > 0 && (
              <Box
                ref={setContainer}
                sx={{
                  mt: 3,
                  maxHeight: isMobile ? "300px" : "400px",
                  overflowY: "auto",
                  pr: 2,
                  scrollBehavior: "smooth",
                }}
              >
                {conversation.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 3,
                      p: isMobile ? 2 : 3,
                      backgroundColor: theme.palette.grey[100],
                      borderRadius: "10px",
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                      boxShadow: theme.shadows[1],
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        mb: 1,
                      }}
                    >
                      Customer Query:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, pl: 2 }}>
                      {item.customerQuery}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        mb: 1,
                      }}
                    >
                      Smart-AI Response:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "pre-line",
                        lineHeight: 1.6,
                        pl: 2,
                      }}
                    >
                      {item.AiResponse}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
              <TextField
                fullWidth
                label="What would you like to know?"
                variant="outlined"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAskQuestion()}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: theme.palette.background.paper,
                  },
                  endAdornment: (
                    <Tooltip title="Get answer">
                      <span>
                        <IconButton
                          onClick={handleAskQuestion}
                          disabled={isLoading || !question}
                          color="primary"
                          sx={{
                            mr: -1,
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: theme.palette.primary.dark,
                            },
                            "&.Mui-disabled": {
                              backgroundColor:
                                theme.palette.action.disabledBackground,
                            },
                          }}
                        >
                          <SendIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ),
                }}
              />
            </Box>
          </Paper>
        )}

        {activeTab === "add" && (
          <Grow in>
            <Paper
              sx={{
                p: isMobile ? 2 : 4,
                borderRadius: "12px",
                backgroundColor: theme.palette.background.default,
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AddIcon color="primary" sx={{ mr: 1.5, fontSize: isMobile ? "1.5rem" : "2rem" }} />
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
                  Expand My Knowledge
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Teach me something new about your business that I can share with
                others
              </Typography>

              <TextField
                fullWidth
                label="What question should I learn to answer?"
                variant="outlined"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />
              <TextField
                fullWidth
                label="What's the correct answer?"
                variant="outlined"
                multiline
                rows={isMobile ? 3 : 5}
                value={newAnswer}
                onChange={(e) => setNewAnswer(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleAddKnowledge}
                  disabled={isAdding || !newQuestion || !newAnswer}
                  startIcon={
                    isAdding ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <AddIcon />
                    )
                  }
                  sx={{
                    borderRadius: "8px",
                    px: isMobile ? 2 : 4,
                    py: isMobile ? 1 : 1.5,
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 600,
                    boxShadow: theme.shadows[2],
                    "&:hover": {
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  {isAdding ? "Saving..." : "Save Knowledge"}
                </Button>
              </Box>
            </Paper>
          </Grow>
        )}

        {activeTab === "knowledge" && (
          <Grow in>
            <Paper
              sx={{
                p: isMobile ? 2 : 4,
                borderRadius: "12px",
                backgroundColor: theme.palette.background.default,
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <HistoryIcon
                  color="primary"
                  sx={{ mr: 1.5, fontSize: isMobile ? "1.5rem" : "2rem" }}
                />
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
                  Knowledge Base
                </Typography>
              </Box>

              {knowledgeBase.length === 0 ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  No knowledge items found. Add some using the "Add Knowledge"
                  tab.
                </Typography>
              ) : (
                <List
                  sx={{
                    maxHeight: isMobile ? "400px" : "500px",
                    overflowY: "auto",
                    pr: 2,
                  }}
                >
                  {knowledgeBase.map((item, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        mb: 2,
                        backgroundColor: theme.palette.grey[100],
                        borderRadius: "8px",
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                      }}
                    >
                      <ListItemText
                        primary={item.question}
                        secondary={item.answer}
                        primaryTypographyProps={{ fontWeight: 500 }}
                        secondaryTypographyProps={{ whiteSpace: "pre-line" }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Edit">
                          <IconButton
                            edge="end"
                            onClick={() => {
                              setCurrentEditItem({
                                originalQuestion: item.question,
                                question: item.question,
                                answer: item.answer,
                              });
                              setEditDialogOpen(true);
                            }}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteKnowledge(item.question)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon color="error" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grow>
        )}

        {activeTab === "fetch" && (
          <Grow in>
            <Paper
              sx={{
                p: isMobile ? 2 : 4,
                borderRadius: "12px",
                backgroundColor: theme.palette.background.default,
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <IdeaIcon color="primary" sx={{ mr: 1.5, fontSize: isMobile ? "1.5rem" : "2rem" }} />
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
                  Analyze and Learn Web Content
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter a website URL to fetch and analyze its content
              </Typography>

              <TextField
                fullWidth
                label="Website URL"
                variant="outlined"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleFetchWebsite}
                  disabled={isFetching || !siteUrl}
                  startIcon={
                    isFetching ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  sx={{
                    borderRadius: "8px",
                    px: isMobile ? 2 : 4,
                    py: isMobile ? 1 : 1.5,
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 600,
                    boxShadow: theme.shadows[2],
                    "&:hover": {
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  {isFetching ? "Learning..." : "Analyze and learn"}
                </Button>
              </Box>

              {fetchedUrls.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 2 }}>
                    Previously Analyzed Pages:
                  </Typography>
                  <List
                    sx={{
                      maxHeight: isMobile ? "200px" : "300px",
                      overflowY: "auto",
                      backgroundColor: theme.palette.grey[100],
                      borderRadius: "8px",
                      p: 2,
                    }}
                  >
                    {fetchedUrls.map((item, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          mb: 1,
                          p: isMobile ? 1 : 2,
                          borderRadius: "4px",
                          backgroundColor: theme.palette.background.paper,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <ListItemText
                          primary={item.site_url}
                          primaryTypographyProps={{
                            fontWeight: 500,
                            color: theme.palette.primary.main,
                            fontSize: isMobile ? "0.875rem" : "1rem",
                          }}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="View Content">
                            <IconButton
                              edge="end"
                              onClick={() =>
                                handleViewContent(item.knowledge.toString())
                              }
                              size={isMobile ? "small" : "medium"}
                            >
                              <VisibilityIcon color="action" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {fetchResult && (
                <Box
                  sx={{
                    mt: 3,
                    p: isMobile ? 2 : 3,
                    backgroundColor: theme.palette.grey[100],
                    borderRadius: "8px",
                    borderLeft: `4px solid ${theme.palette.secondary.main}`,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      mb: 1,
                    }}
                  >
                    Fetched Content:
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                    {fetchResult}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grow>
        )}

        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Edit Knowledge</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Question"
              fullWidth
              variant="outlined"
              value={currentEditItem.question}
              onChange={(e) =>
                setCurrentEditItem((prev) => ({
                  ...prev,
                  question: e.target.value,
                }))
              }
              sx={{ mb: 3 }}
            />
            <TextField
              margin="dense"
              label="Answer"
              fullWidth
              variant="outlined"
              multiline
              rows={isMobile ? 6 : 4}
              value={currentEditItem.answer}
              onChange={(e) =>
                setCurrentEditItem((prev) => ({
                  ...prev,
                  answer: e.target.value,
                }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditDialogOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditKnowledge}
              variant="contained"
              disabled={
                isEditing ||
                !currentEditItem.question ||
                !currentEditItem.answer
              }
              startIcon={
                isEditing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{ textTransform: "none" }}
            >
              {isEditing ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        <Box
          sx={{
            mt: 4,
            p: isMobile ? 2 : 3,
            backgroundColor: theme.palette.grey[100],
            borderRadius: "12px",
            textAlign: "center",
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <SparkleIcon color="primary" sx={{ fontSize: isMobile ? "1.5rem" : "2rem", mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            Pro Tip
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The more specific questions you add, the better I can assist your
            customers!
          </Typography>
        </Box>
      </Box>
    </Fade>
  );
};

export default AIBot;